import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bell, BellRinging, Check } from "@phosphor-icons/react";

export default function NotificationsBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const wrapRef = useRef(null);

  const fetchCount = async () => {
    try { const { data } = await api.get("/notifications/unread-count"); setCount(data.count); } catch {}
  };
  const fetchItems = async () => {
    try { const { data } = await api.get("/notifications?limit=20"); setItems(data); } catch {}
  };

  useEffect(() => {
    if (!user) return;
    fetchCount();
    const t = setInterval(fetchCount, 30000); // poll every 30s
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    if (!open) return;
    fetchItems();
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setCount(0);
    setItems((arr) => arr.map((n) => ({ ...n, read: true })));
  };
  const markOne = async (id) => {
    await api.post(`/notifications/${id}/read`);
    setCount((c) => Math.max(0, c - 1));
    setItems((arr) => arr.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };

  if (!user) return null;

  return (
    <div className="relative" ref={wrapRef}>
      <button onClick={() => setOpen((v) => !v)} data-testid="notifications-bell"
        className="relative p-2 rounded-full hover:bg-black/5 transition">
        {count > 0 ? <BellRinging size={20} weight="fill" className="text-[#E05D3A]" /> : <Bell size={20} />}
        {count > 0 && (
          <span data-testid="notifications-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-[#E05D3A] text-white text-[10px] font-bold rounded-full inline-flex items-center justify-center">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notifications-panel"
          className="absolute right-0 mt-2 w-80 sm:w-96 bg-white border border-[#E2E0D8] rounded-2xl shadow-2xl z-50 max-h-[80vh] overflow-hidden flex flex-col">
          <div className="px-4 py-3 flex items-center justify-between border-b border-[#E2E0D8]">
            <div className="font-heading font-bold">Notifications</div>
            {count > 0 && (
              <button onClick={markAll} data-testid="notifications-mark-all"
                className="text-xs font-bold text-[#E05D3A] inline-flex items-center gap-1">
                <Check size={12} weight="bold" /> Mark all read
              </button>
            )}
          </div>
          <div className="overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#0F2537]/60">No notifications yet.</div>
            ) : (
              <ul className="divide-y divide-[#E2E0D8]">
                {items.map((n) => {
                  const inner = (
                    <div className={`px-4 py-3 hover:bg-black/[0.02] ${!n.read ? "bg-[#E05D3A]/5" : ""}`}>
                      <div className="flex items-start gap-2">
                        {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-[#E05D3A] shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm ${!n.read ? "font-bold" : "font-semibold"}`}>{n.title}</div>
                          <div className="text-xs text-[#0F2537]/60 mt-0.5">{n.body}</div>
                          <div className="text-[10px] text-[#0F2537]/40 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                        </div>
                      </div>
                    </div>
                  );
                  return (
                    <li key={n.id} data-testid={`notif-${n.id}`} onClick={() => !n.read && markOne(n.id)}>
                      {n.link ? <Link to={n.link} onClick={() => setOpen(false)}>{inner}</Link> : inner}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="border-t border-[#E2E0D8] px-4 py-2 text-center">
            <Link to="/notifications" onClick={() => setOpen(false)} data-testid="notifications-view-all"
              className="text-xs font-bold text-[#0F2537] hover:text-[#E05D3A]">View all →</Link>
          </div>
        </div>
      )}
    </div>
  );
}
